import { useMemo } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { request, requestList, orgBase } from '@/api/http'
import type { Evidence, LevelCode, ObjectiveState } from '@/api/types/readiness'
import { emptyState } from '@/lib/readiness/scope'

// ── qtz-readiness assessments REST client ───────────────────────────────────
// QRI assessments served by qtz-readiness under /api/v1/organizations/:orgId
// (Vite dev proxy in development, nginx in production). Types mirror the Go
// models in qtz-readiness/internal/assessment/model.go.

export type AssessmentStatus = 'draft' | 'submitted' | 'pending_approval' | 'approved' | 'returned'
// Named apart from Evidence's EvidenceSource in @/api/types/readiness, which
// describes the in-browser engine's evidence rather than the service's.
// playbook appears on cells only, when Playbook data backs the objective
// (TE-1, EE-1); it is never linked evidence.
export type AssessmentEvidenceSource = 'manual' | 'discovery' | 'playbook' | 'risk' | 'readiness'

export interface Assessment {
  id: string
  organization_id: string
  framework_version_id: string
  title: string
  as_of_date: string // YYYY-MM-DD
  lead_assessor_id: string
  source: 'csa_report' | 'internal_judgement'
  csa_report_attachment_id: string | null
  status: AssessmentStatus
  previous_assessment_id: string | null
  submitted_at: string | null
  submitted_by: string | null
  row_version: number
  created_at: string
  updated_at: string
}

// One heatmap cell: the assessment's level for one objective.
export interface AssessmentCell {
  id: string
  domain_code: string
  domain_name: string
  objective_code: string
  objective_no: number // 1 or 2; objective 1 is the no-regrets one
  objective_title: string
  is_no_regrets: boolean
  level: number | null // 0–3, null when not assessed (never shown as L0)
  previous_level: number | null
  movement: 'up' | 'down' | 'unchanged' | null // null when either level is unset
  colour_hex: string
  carried_forward: boolean
  evidence_count: number
  evidence_sources: AssessmentEvidenceSource[] | null
  // The level the synced Discovery and Playbook data supports; null for an
  // objective set by hand only. A sync never changes `level`.
  suggested_level: number | null
  suggestion_reason: string | null
  suggestion_source: 'discovery' | 'playbook' | null
  suggested_at: string | null
  // The Assessor kept their level against this suggestion.
  suggestion_dismissed: boolean
  needs_confirmation: boolean
  review_reason?: ReviewReason
}

// Why a cell needs the Assessor: a sync suggests a different level, the level
// was carried forward from the previous assessment, or it has no evidence.
export type ReviewReason = 'suggestion' | 'carried_forward' | 'no_evidence'

export const REVIEW_REASON_LABEL: Record<ReviewReason, string> = {
  suggestion: 'New suggested level',
  carried_forward: 'Carried over from the last assessment',
  no_evidence: 'No evidence yet',
}

// One line of an objective's history (GET …/objectives/:code/history).
export interface HistoryEntry {
  id: number // 0 for a level recorded before history was kept
  kind: 'level_set' | 'accepted' | 'kept' | 'suggested' | 'input_updated' // input_updated: GV-1 governance input changed
  from_level: number | null
  to_level: number | null
  note: string | null
  source: 'discovery' | 'playbook' | null
  actor: { id: string; display_name: string } | null // null for a sync
  occurred_at: string
  assessment: { id: string; title: string; as_of_date: string }
}

// What changed on one objective since a time (GET …/activity).
export interface ActivityEntry extends HistoryEntry {
  objective_code: string
}

export interface AssessmentHeatmap {
  assessment: Assessment
  domains: Array<{ code: string; name: string; cells: AssessmentCell[] }>
}

// One piece of evidence linked to a cell. Which optional fields are set
// depends on kind: a file, a link or a Quantizant record.
export interface AssessmentEvidenceItem {
  id: string
  kind: string
  source: AssessmentEvidenceSource
  title: string
  as_of: string
  url: string | null
  file_name: string | null
  record_label: string | null
  carried_forward: boolean
  added_at: string
}

// What the objective drawer shows for one cell (GET …/objectives/:code).
export interface AssessmentObjectiveDetail extends AssessmentCell {
  assessment_id: string
  objective_scope: string
  objective_rationale: string
  rationale: string | null // the Assessor's note on the level
  set_at: string | null
  set_by: { id: string; display_name: string } | null
  evidence: AssessmentEvidenceItem[] | null
  // Each level with CSA's answer statements for it, one per question, verbatim.
  levels: Array<{ level: number; statements: Array<{ question_no: number; short_label: string; letter: string; text: string }> }> | null
  // Steps from the current level to the next; empty until that content is loaded.
  next_steps: Array<{ text: string; handbook_ref: string | null }> | null
  // GV-1 only: its two parts, Discovery owner metadata and the governance input.
  governance?: GovernanceDetail
  // Whether the signed-in person may set levels and record inputs here;
  // absent from older services, where the write itself decides.
  can_assess?: boolean
}

// GV-1 part ii, entered by an Assessor. Owner, decision authority and RACI or
// charter are needed for the part to count; the oversight body is optional.
export interface GovernanceInput {
  programme_owner: string
  decision_authority: string
  oversight_body: string
  raci_reference: string
}

export type OwnerMetadataField = 'business_owner' | 'technology_owner' | 'business_unit' | 'department' | 'criticality' | 'regulatory_scope'

export const OWNER_METADATA_LABEL: Record<OwnerMetadataField, string> = {
  business_owner: 'Business owner',
  technology_owner: 'Technical owner',
  business_unit: 'Business unit',
  department: 'Department',
  criticality: 'Criticality',
  regulatory_scope: 'Regulatory scope',
}

export interface GovernanceDetail {
  input: GovernanceInput
  updated_by: { id: string; display_name: string } | null
  updated_at: string | null
  // Coverage of the six owner fields on the systems in scope; null when Discovery is not connected.
  metadata: { scope: 'prioritised' | 'all'; system_count: number; complete_count: number; missing_by_field: Partial<Record<OwnerMetadataField, number>> } | null
  part_one_met: boolean
  part_two_met: boolean
}

// CSA's verbatim statements per level, as the drawer's full wording shows them.
export const csaWordingOf = (d: AssessmentObjectiveDetail | null): Partial<Record<LevelCode, Array<{ label: string; text: string }>>> | null => {
  if (!d?.levels?.length) return null
  return Object.fromEntries(d.levels.map((l) => [`L${l.level}`, l.statements.map((st) => ({ label: st.short_label, text: st.text }))]))
}

const base = (orgId: string) => `${orgBase(orgId)}/assessments`

export const assessmentsApi = {
  // Newest as-of date first, without cells.
  list(orgId: string): Promise<Assessment[]> {
    return requestList(base(orgId))
  },
  heatmap(orgId: string, assessmentId: string): Promise<AssessmentHeatmap> {
    return request(`${base(orgId)}/${assessmentId}/heatmap`)
  },
  objective(orgId: string, assessmentId: string, code: string): Promise<AssessmentObjectiveDetail> {
    return request(`${base(orgId)}/${assessmentId}/objectives/${code}`)
  },
  evidence(orgId: string, assessmentId: string, code: string): Promise<AssessmentEvidenceItem[]> {
    return requestList(`${base(orgId)}/${assessmentId}/objectives/${code}/evidence`)
  },
  // Sets the level by hand with the reason; attributed to the signed-in user.
  setLevel(orgId: string, assessmentId: string, code: string, body: { level: number; rationale: string }): Promise<AssessmentObjectiveDetail> {
    return request(`${base(orgId)}/${assessmentId}/objectives/${code}/level`, { method: 'PUT', body: JSON.stringify(body) })
  },
  // accept takes the suggested level; keep confirms the level set.
  review(orgId: string, assessmentId: string, code: string, body: { decision: 'accept' | 'keep'; note: string }): Promise<AssessmentObjectiveDetail> {
    return request(`${base(orgId)}/${assessmentId}/objectives/${code}/review`, { method: 'POST', body: JSON.stringify(body) })
  },
  history(orgId: string, assessmentId: string, code: string): Promise<HistoryEntry[]> {
    return requestList(`${base(orgId)}/${assessmentId}/objectives/${code}/history`)
  },
  // Saves the draft as a dated, read-only record; a working draft continues from it.
  save(orgId: string, assessmentId: string, body: { title: string; as_of_date: string }): Promise<{ saved: Assessment; next: Assessment }> {
    return request(`${base(orgId)}/${assessmentId}/save`, { method: 'POST', body: JSON.stringify(body) })
  },
  // Records GV-1's governance input; returns GV-1 with its refreshed suggestion.
  setGovernance(orgId: string, assessmentId: string, body: GovernanceInput): Promise<AssessmentObjectiveDetail> {
    return request(`${base(orgId)}/${assessmentId}/governance`, { method: 'PUT', body: JSON.stringify(body) })
  },
  activity(orgId: string, assessmentId: string, since: string): Promise<ActivityEntry[]> {
    return requestList(`${base(orgId)}/${assessmentId}/activity?since=${encodeURIComponent(since)}`)
  },
}

export const levelCode = (n: number | null | undefined): LevelCode | null => (n == null ? null : (`L${n}` as LevelCode))

export const cellLevel = (c: AssessmentCell): LevelCode => `L${c.level ?? 0}` as LevelCode

// Readiness's own records are entered by hand in this module, so they read as Manual.
const SOURCE_LABEL: Record<AssessmentEvidenceSource, string> = { manual: 'Manual', discovery: 'Discovery', playbook: 'Playbook', risk: 'Risk', readiness: 'Manual' }

// A cell's evidence sources as shown beside its evidence count, e.g. "Discovery, Manual".
export const remoteSourceLabel = (c: AssessmentCell): string | null => {
  const sources = Array.from(new Set((c.evidence_sources ?? []).map((s) => SOURCE_LABEL[s] ?? s)))
  return sources.length ? sources.join(', ') : null
}

// The page-wide ObjectiveState for a served cell, so every widget that reads
// states (summary tiles, sidebar counts, cards, drawer) agrees with the heatmap.
// A pending sync suggestion keeps the level set and carries the suggestion
// beside it; a cell not yet assessed with a suggestion is a suggested state.
// A level that needs confirming for another reason (carried forward, no
// evidence) maps to a suggestion of its own level, which is what
// needsConfirmation() and liveLevel() expect; an unassessed cell stays unset.
export function stateFromCell(c: AssessmentCell, a: Assessment, detail?: AssessmentObjectiveDetail | null): ObjectiveState {
  const s = emptyState(a.organization_id, 'csa-qri-v1', c.objective_code)
  const basis = a.source === 'csa_report' ? 'csa_report' : 'internal'
  const pendingSuggestion = c.needs_confirmation && c.review_reason === 'suggestion' ? levelCode(c.suggested_level) : null
  const suggestion = { suggested_at: c.suggested_at, suggested_reason: c.suggestion_reason }
  if (c.level == null) {
    return pendingSuggestion ? { ...s, ...suggestion, basis, status: 'suggested', suggested_level: pendingSuggestion } : { ...s, basis }
  }
  const level = cellLevel(c)
  const set = { set_by: detail?.set_by?.display_name ?? null, set_at: detail?.set_at ?? null, note: detail?.rationale ?? null }
  if (pendingSuggestion) return { ...s, ...set, ...suggestion, basis, level, status: 'declared', suggested_level: pendingSuggestion }
  return c.needs_confirmation
    ? { ...s, ...set, basis, level, status: 'suggested', suggested_level: level, suggested_at: a.updated_at, suggested_reason: c.review_reason ? `${REVIEW_REASON_LABEL[c.review_reason]}.` : null }
    : { ...s, ...set, basis, level, status: 'declared' }
}

// The service's evidence in the portal's Evidence shape. Quantizant records
// (discovery, risk, readiness) all filter as Discovery, Playbook's as Playbook;
// files and links as manual. source_label keeps the item's own name for display.
export function evidenceFromItem(e: AssessmentEvidenceItem, orgId: string, code: string): Evidence {
  return {
    id: e.id,
    org_id: orgId,
    index_id: 'csa-qri-v1',
    objective_code: code,
    source: e.source === 'manual' || e.source === 'playbook' ? e.source : 'discovery',
    external_id: null,
    title: e.title,
    summary: e.record_label ?? e.kind,
    link: e.url,
    occurred_at: e.as_of,
    added_by: e.source === 'manual' ? 'manual' : 'system',
    archived_at: null,
    kind: e.kind === 'file' ? 'file' : undefined,
    file_name: e.file_name,
    source_label: e.kind === 'link' ? 'Link' : e.kind === 'file' ? 'File' : SOURCE_LABEL[e.source],
  }
}

// One cell's drawer detail, fetched while the drawer shows a served cell.
export function useAssessmentObjective(orgId: string, assessmentId: string | null, code: string | null) {
  return useQuery({
    queryKey: ['readiness-objective', orgId, assessmentId, code],
    queryFn: () => assessmentsApi.objective(orgId, assessmentId!, code!),
    enabled: !!assessmentId && !!code,
    retry: false,
  })
}

// An organisation has at most one open assessment (draft, pending or returned).
const OPEN: AssessmentStatus[] = ['draft', 'pending_approval', 'returned']

// Whether levels can still change: a draft or returned assessment.
export const isEditable = (status: AssessmentStatus) => status === 'draft' || status === 'returned'

// The heatmap of the organisation's open assessment, else its newest one. The
// service holds QRI assessments only, so the other indexes stay on the
// in-browser engine (status 'off').
export function useAssessmentHeatmap(orgId: string, indexId: string) {
  const enabled = !!orgId && indexId === 'csa-qri-v1'
  const q = useQuery({
    queryKey: ['readiness-heatmap', orgId],
    queryFn: async () => {
      const all = await assessmentsApi.list(orgId)
      const pick = all.find((a) => OPEN.includes(a.status)) ?? all[0]
      if (!pick) return null
      // The assessment it follows, whose levels the cells carry as previous_level.
      const previous = all.find((a) => a.id === pick.previous_assessment_id) ?? null
      return { heatmap: await assessmentsApi.heatmap(orgId, pick.id), previous }
    },
    enabled,
    retry: false,
  })
  const heatmap = q.data?.heatmap ?? null
  const cells = useMemo(() => (heatmap ? new Map(heatmap.domains.flatMap((d) => d.cells).map((c) => [c.objective_code, c])) : null), [heatmap])
  const states = useMemo(() => (heatmap ? Object.fromEntries(Array.from(cells ?? []).map(([code, c]) => [code, stateFromCell(c, heatmap.assessment)])) : null), [heatmap, cells])
  const status = !enabled ? 'off' : q.isPending ? 'loading' : q.isError ? 'error' : heatmap ? 'ready' : 'empty'
  return { status, heatmap, previous: q.data?.previous ?? null, cells, states, error: q.error } as const
}

// One objective's history, newest first, while its drawer is open.
export function useObjectiveHistory(orgId: string, assessmentId: string | null, code: string | null) {
  return useQuery({
    queryKey: ['readiness-history', orgId, assessmentId, code],
    queryFn: () => assessmentsApi.history(orgId, assessmentId!, code!),
    enabled: !!assessmentId && !!code,
    retry: false,
  })
}

// Every served cell's linked evidence, for the summary report.
export function useAssessmentEvidence(orgId: string, assessmentId: string | null, codes: string[]) {
  const qs = useQueries({
    queries: codes.map((code) => ({
      queryKey: ['readiness-evidence', orgId, assessmentId, code],
      queryFn: () => assessmentsApi.evidence(orgId, assessmentId!, code),
      enabled: !!assessmentId,
      retry: false,
    })),
  })
  return useMemo(() => new Map(codes.map((code, i) => [code, qs[i]?.data ?? []])), [codes, qs])
}

// The writes the drawer makes on a served cell. Each refreshes the heatmap, the
// drawer and the objective's history.
export function useObjectiveWrites(orgId: string, assessmentId: string | null, code: string | null) {
  const qc = useQueryClient()
  const refresh = () =>
    Promise.all(['readiness-heatmap', 'readiness-objective', 'readiness-history'].map((key) => qc.invalidateQueries({ queryKey: [key, orgId] })))
  const setLevel = useMutation({
    mutationFn: (body: { level: number; rationale: string }) => assessmentsApi.setLevel(orgId, assessmentId!, code!, body),
    onSuccess: refresh,
  })
  const review = useMutation({
    mutationFn: (body: { decision: 'accept' | 'keep'; note: string }) => assessmentsApi.review(orgId, assessmentId!, code!, body),
    onSuccess: refresh,
  })
  const setGovernance = useMutation({
    mutationFn: (body: GovernanceInput) => assessmentsApi.setGovernance(orgId, assessmentId!, body),
    onSuccess: refresh,
  })
  return { setLevel, review, setGovernance }
}

// Saving refreshes everything that reads the open assessment.
export function useSaveAssessment(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ assessmentId, ...body }: { assessmentId: string; title: string; as_of_date: string }) => assessmentsApi.save(orgId, assessmentId, body),
    onSuccess: () => Promise.all(['readiness-heatmap', 'readiness-objective', 'readiness-history'].map((key) => qc.invalidateQueries({ queryKey: [key, orgId] }))),
  })
}
